/**
 * CustomerDetailPage - Detailansicht eines Kunden
 * 
 * Strukturierte Sektionen:
 * - Grunddaten (Name, Kürzel)
 * - Identifikation (Kundennr., Lieferadr.-Nr. - read-only, ERP-geführt)
 * - Organisation (Typ, Gruppe)
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Building2, ArrowLeft, Save, AlertCircle, Users, Info, Trash2, AlertTriangle } from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';

interface CustomerGroup {
  id: string;
  name: string;
}

interface Customer {
  id: string;
  name: string;
  shortName: string | null;
  externalDeliveryId: string | null;
  externalCustomerNo: string | null;
  type: 'REGULAR' | 'COLLECTIVE' | 'RESIDENT';
  isActive: boolean;
  customerGroupId: string | null;
  customerGroup: CustomerGroup | null;
}

type CustomerType = 'REGULAR' | 'COLLECTIVE' | 'RESIDENT';

const TYPE_LABELS: Record<CustomerType, string> = {
  REGULAR: 'Stammkunde',
  COLLECTIVE: 'Sammelkunde',
  RESIDENT: 'Heimbewohner',
};

const TYPE_OPTIONS: { value: CustomerType; label: string }[] = [
  { value: 'REGULAR', label: 'Stammkunde' },
  { value: 'COLLECTIVE', label: 'Sammelkunde' },
  { value: 'RESIDENT', label: 'Heimbewohner' },
];

interface FormData {
  name: string;
  shortName: string;
  type: CustomerType;
  customerGroupId: string;
  /** Nur bei manueller Neuanlage erfassbar; bei ERP-importierten Kunden read-only. */
  externalCustomerNo: string;
}

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === 'new';
  
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [customerGroups, setCustomerGroups] = useState<CustomerGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    shortName: '',
    type: 'REGULAR',
    customerGroupId: '',
    externalCustomerNo: '',
  });

  // Load customer details
  const loadCustomer = async () => {
    if (isNew) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/customers/${id}`, { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCustomer(data);
        setFormData({
          name: data.name,
          shortName: data.shortName || '',
          type: data.type,
          customerGroupId: data.customerGroupId || '',
          externalCustomerNo: data.externalCustomerNo || '',
        });
      } else if (res.status === 404) {
        navigate('/admin/customers');
      }
    } catch (err) {
      console.error('Fehler beim Laden des Kunden:', err);
      setError('Kunde konnte nicht geladen werden');
    }
    setLoading(false);
  };

  // Load customer groups
  const loadCustomerGroups = async () => {
    try {
      const res = await fetch('/api/admin/customer-groups', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setCustomerGroups(data.filter((g: CustomerGroup & { isActive: boolean }) => g.isActive));
      }
    } catch (err) {
      console.error('Fehler beim Laden der Kundengruppen:', err);
    }
  };

  useEffect(() => {
    loadCustomer();
    loadCustomerGroups();
  }, [id]);

  // Track changes
  const updateField = <K extends keyof FormData>(key: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
    setError(null);
  };

  // Save handler
  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Name ist erforderlich');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const payload: Record<string, unknown> = {
        name: formData.name.trim(),
        shortName: formData.shortName.trim() || null,
        type: formData.type,
        customerGroupId: formData.customerGroupId || null,
      };

      // Kundennummer nur bei der manuellen Neuanlage mitschicken. Bei bestehenden
      // Kunden bleibt sie ERP-gefuehrt und wird hier nicht ueberschrieben.
      if (isNew && formData.externalCustomerNo.trim()) {
        payload.externalCustomerNo = formData.externalCustomerNo.trim();
      }

      const url = isNew ? '/api/admin/customers' : `/api/admin/customers/${id}`;
      const method = isNew ? 'POST' : 'PATCH';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        const saved = await res.json();
        if (isNew) {
          navigate(`/admin/customers/${saved.id}`, { replace: true });
        } else {
          setCustomer(saved);
          setHasChanges(false);
        }
      } else {
        const err = await res.json().catch(() => ({ error: 'Speichern fehlgeschlagen' }));
        setError(err.error || 'Speichern fehlgeschlagen');
      }
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
      setError('Netzwerkfehler beim Speichern');
    }

    setSaving(false);
  };

  // Delete (soft delete)
  const handleDelete = async () => {
    if (!id || isNew) return;
    
    setDeleting(true);
    try {
      const res = await fetch(`/api/admin/customers/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      
      if (res.ok) {
        navigate('/admin/customers');
      }
    } catch (err) {
      console.error('Fehler beim Deaktivieren:', err);
    }
    setDeleting(false);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-8 text-center text-slate-500">Laden...</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Link
            to="/admin/customers"
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="w-6 h-6" />
              {isNew ? 'Neuer Kunde' : customer?.name || 'Kunde'}
            </h1>
            {!isNew && customer?.externalCustomerNo && (
              <p className="text-sm text-slate-500 mt-0.5">
                Kundennr. {customer.externalCustomerNo}
                {customer.externalDeliveryId && ` · Liefernr. ${customer.externalDeliveryId}`}
              </p>
            )}
          </div>
          <button
            onClick={handleSave}
            disabled={saving || (!hasChanges && !isNew)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Speichern...' : 'Speichern'}
          </button>
        </div>

        {/* Error */}
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <p className="text-red-800">{error}</p>
          </div>
        )}

        {/* Sektion: Grunddaten */}
        <section className="bg-white rounded-lg border p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 border-b pb-2">Grunddaten</h2>
          
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="z.B. Hotel Strandperle"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Kürzel
              </label>
              <input
                type="text"
                value={formData.shortName}
                onChange={(e) => updateField('shortName', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="z.B. HSP"
              />
            </div>
          </div>
        </section>

        {/* Sektion: Identifikation bei manueller Neuanlage (Meldung #18) */}
        {isNew && (
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h2 className="text-lg font-semibold text-slate-900">Identifikation</h2>
              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">optional</span>
            </div>

            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex items-start gap-2">
              <Info className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
              <p className="text-xs text-slate-600">
                Bei manuell angelegten Kunden können Sie die Kundennummer direkt eintragen —
                ein Import ist dafür nicht nötig. Nach dem Speichern ist sie ERP-geführt und
                wird hier nicht mehr bearbeitet.
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Kundennr.
                </label>
                <input
                  type="text"
                  value={formData.externalCustomerNo}
                  onChange={(e) => updateField('externalCustomerNo', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg font-mono focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="z.B. 10042"
                />
              </div>
            </div>
          </section>
        )}

        {/* Sektion: Identifikation (read-only) */}
        {!isNew && (
          <section className="bg-white rounded-lg border p-6 space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h2 className="text-lg font-semibold text-slate-900">Identifikation</h2>
              <span className="text-xs text-slate-500 bg-slate-100 px-2 py-1 rounded">ERP-geführt</span>
            </div>
            
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200 flex items-start gap-2">
              <Info className="w-4 h-4 text-slate-500 shrink-0 mt-0.5" />
              <p className="text-xs text-slate-600">
                Diese Felder werden aus dem ERP-System importiert und können hier nicht bearbeitet werden.
              </p>
            </div>
            
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">
                  Kundennr.
                </label>
                <input
                  type="text"
                  value={customer?.externalCustomerNo || '–'}
                  disabled
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-600 font-mono"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-slate-500 mb-1">
                  Lieferadr.-Nr.
                </label>
                <input
                  type="text"
                  value={customer?.externalDeliveryId || '–'}
                  disabled
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg bg-slate-50 text-slate-600 font-mono"
                />
              </div>
            </div>
          </section>
        )}

        {/* Sektion: Organisation */}
        <section className="bg-white rounded-lg border p-6 space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 border-b pb-2">Organisation</h2>
          
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Kundentyp
              </label>
              <select
                value={formData.type}
                onChange={(e) => updateField('type', e.target.value as CustomerType)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {TYPE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Kundengruppe
              </label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <select
                  value={formData.customerGroupId}
                  onChange={(e) => updateField('customerGroupId', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Keine Gruppe</option>
                  {customerGroups.map(g => (
                    <option key={g.id} value={g.id}>{g.name}</option>
                  ))}
                </select>
              </div>
              {formData.customerGroupId && (
                <Link
                  to={`/admin/customer-groups/${formData.customerGroupId}`}
                  className="inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-1"
                >
                  <Users className="w-3 h-3" />
                  Gruppe öffnen
                </Link>
              )}
            </div>
          </div>
        </section>

        {/* Gefahrenzone (nur bei Edit) */}
        {!isNew && customer && (
          <section className="bg-white rounded-lg border border-red-200 p-6">
            <h2 className="font-semibold text-red-600 mb-2">Gefahrenzone</h2>
            <p className="text-sm text-slate-600 mb-4">
              Das Deaktivieren eines Kunden entfernt ihn aus der Auswahl bei der Erfassung.
              Bereits erfasste Waschvorgaenge bleiben unveraendert.
            </p>
            <button
              onClick={() => setDeleteConfirmOpen(true)}
              className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Kunde deaktivieren
            </button>
          </section>
        )}

        {/* Hinweis */}
        {!isNew && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
            <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <p className="font-medium">Hinweis zu historischen Daten</p>
              <p className="mt-1">
                Änderungen am Kundenstamm wirken sich nicht auf bereits erfasste Waschvorgänge aus. 
                Diese enthalten Snapshot-Daten zum Zeitpunkt der Erfassung.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirm Modal */}
      {deleteConfirmOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="p-4 border-b flex items-center gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600" />
              <h2 className="text-lg font-semibold text-red-600">Kunde deaktivieren?</h2>
            </div>
            <div className="p-4">
              <p className="text-slate-600">
                <strong>"{customer?.name}"</strong> wird deaktiviert.
              </p>
              <p className="text-sm text-slate-500 mt-2">
                Der Kunde kann nicht mehr fuer neue Erfassungen verwendet werden.
              </p>
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-slate-50 rounded-b-xl">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100"
              >
                Abbrechen
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
              >
                {deleting ? 'Deaktivieren...' : 'Deaktivieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
